"use client";

import { useState, useRef, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { v4 as uuidv4 } from "uuid";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { saveTierList, saveImage, resolveImage } from "@/lib/indexdb";
import { TierList, Image } from "@/types/tier-list";
import { ColorConfigModal } from "@/components/color-config-modal";
import { TiersSection } from "@/components/tiers-section";
import { LogoUpload, LogoUploadRef } from "@/components/logo-upload";
import { useTierManagement } from "@/hooks/use-tier-management";

const hexColorRegex = /^#[0-9A-Fa-f]{6}$/;

const tierSchema = z.object({
  id: z.string(),
  label: z.string().min(1, "Label is required"),
  labelColor: z
    .string()
    .regex(hexColorRegex, "Must be a valid hex color (e.g., #FF0000)"),
  backgroundColor: z
    .string()
    .regex(hexColorRegex, "Must be a valid hex color (e.g., #FFCCCC)"),
});

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const formSchema = z.object({
  name: z.string().min(1, "Tier list name is required"),
  tiers: z.array(tierSchema).min(1, "At least one tier is required"),
  logo: z
    .any()
    .optional()
    .refine(
      (files) =>
        !files || files?.length === 0 || files?.[0]?.type?.startsWith("image/"),
      "Logo must be an image file"
    )
    .refine(
      (files) =>
        !files || files?.length === 0 || files?.[0]?.size <= MAX_FILE_SIZE,
      "Logo must be under 10MB"
    ),
});

type FormValues = z.infer<typeof formSchema>;

interface EditTierListFormProps {
  tierList: TierList;
  onSuccess: (tierListId: string) => void;
  onCancel: () => void;
}

export function EditTierListForm({
  tierList,
  onSuccess,
  onCancel,
}: EditTierListFormProps) {
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [hasNewLogo, setHasNewLogo] = useState(false);
  const [submitError, setSubmitError] = useState<string>("");
  const logoUploadRef = useRef<LogoUploadRef>(null);

  // Convert existing tiers to form format
  const initialTiers = tierList.tiers
    .sort((a, b) => a.priority - b.priority)
    .map((tier) => ({
      id: tier.id,
      label: tier.label,
      labelColor: tier.color.label,
      backgroundColor: tier.color.background,
    }));

  const tierManagement = useTierManagement(initialTiers);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: tierList.name,
      tiers: initialTiers,
    },
  });

  // Load existing logo
  useEffect(() => {
    const loadLogo = async () => {
      if (tierList.logoId) {
        const url = await resolveImage(tierList.logoId);
        if (url) {
          setLogoPreview(url);
        }
      }
    };
    loadLogo();

    return () => {
      if (logoPreview && hasNewLogo) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Clear previous errors
    setSubmitError("");

    // Validate localTiers manually
    const hasEmptyLabel = tierManagement.localTiers.some((tier) => !tier.label.trim());
    if (hasEmptyLabel) {
      setSubmitError(
        "All tiers must have a label. Please fill in all tier labels."
      );
      return;
    }

    // Trigger form validation
    const isValid = await form.trigger();
    if (!isValid) {
      return;
    }

    const values = form.getValues();

    try {
      let logoId = tierList.logoId;

      // If user uploaded a new logo, save it
      if (hasNewLogo && values.logo && values.logo.length > 0) {
        const logoFile = values.logo[0];
        logoId = uuidv4();

        const image: Image = {
          id: logoId,
          tierListId: tierList.id,
          blob: logoFile,
          mimeType: logoFile.type,
        };
        await saveImage(image);
      }

      // Update tier list
      const updatedTierList: TierList = {
        id: tierList.id,
        name: values.name,
        tiers: tierManagement.localTiers.map((tier, index) => ({
          id: tier.id,
          label: tier.label,
          priority: index,
          color: {
            label: tier.labelColor as `#${string}`,
            background: tier.backgroundColor as `#${string}`,
          },
        })),
        logoId,
      };

      await saveTierList(updatedTierList);
      onSuccess(tierList.id);
    } catch (error) {
      console.error("Failed to update tier list:", error);
      setSubmitError("Failed to update tier list. Please try again.");
    }
  };

  const handleLogoChange = (files: FileList | null) => {
    if (files && files.length > 0) {
      form.setValue("logo", files);
      setHasNewLogo(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(files[0]);
    }
  };

  const handleLogoRemove = () => {
    if (hasNewLogo && logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoPreview(null);
    setHasNewLogo(false);
    form.setValue("logo", null);
    logoUploadRef.current?.clearInput();
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="name">Tier List Name</Label>
          <Controller
            control={form.control}
            name="name"
            render={({ field, fieldState }) => (
              <>
                <Input
                  id="name"
                  placeholder="My Awesome Tier List"
                  {...field}
                />
                {fieldState.error && (
                  <p className="text-sm text-destructive">
                    {fieldState.error.message}
                  </p>
                )}
              </>
            )}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="logo">Logo</Label>
          <Controller
            control={form.control}
            name="logo"
            render={({ fieldState }) => (
              <LogoUpload
                ref={logoUploadRef}
                logoPreview={logoPreview}
                onLogoChange={handleLogoChange}
                onLogoRemove={handleLogoRemove}
                error={fieldState.error?.message}
              />
            )}
          />
        </div>

        <TiersSection
          tiers={tierManagement.localTiers}
          onLabelChange={tierManagement.handleLabelChange}
          openColorModal={tierManagement.openColorModal}
          removeTier={tierManagement.removeTier}
          addTier={tierManagement.addTier}
          onDragStart={tierManagement.handleDragStart}
          onDragEnd={tierManagement.handleDragEnd}
          activeId={tierManagement.activeId}
        />

        {submitError && (
          <p className="text-sm text-destructive">{submitError}</p>
        )}

        <div className="flex gap-2 justify-end">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit">Save Changes</Button>
        </div>
      </form>
      <ColorConfigModal
        isOpen={tierManagement.colorModalOpen}
        onClose={tierManagement.closeColorModal}
        initialLabelColor={
          tierManagement.editingTierIndex !== null &&
          tierManagement.localTiers[tierManagement.editingTierIndex]
            ? tierManagement.localTiers[tierManagement.editingTierIndex]
                .labelColor
            : "#000000"
        }
        initialBackgroundColor={
          tierManagement.editingTierIndex !== null &&
          tierManagement.localTiers[tierManagement.editingTierIndex]
            ? tierManagement.localTiers[tierManagement.editingTierIndex]
                .backgroundColor
            : "#CCCCCC"
        }
        onSave={tierManagement.saveColors}
        tierLabel={
          tierManagement.editingTierIndex !== null &&
          tierManagement.localTiers[tierManagement.editingTierIndex]
            ? tierManagement.localTiers[tierManagement.editingTierIndex].label ||
              `${tierManagement.editingTierIndex + 1}`
            : ""
        }
      />
    </>
  );
}
